import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../../shared/context/UserContext';
import { useReferenceData } from '../../../shared/hooks/useReferenceData';
import { ComboBox } from '../../../shared/components/ComboBox';
import { FixedListSelect } from '../../../shared/components/FixedListSelect';
import {
  getToolDetail, updateInventoryTool, getMedia, deleteMedia, getSites,
  getPartNumbers, addPartNumber, patchPartNumber, deletePartNumber,
  getAssetTags, addAssetTag, patchAssetTag, deleteAssetTag,
} from '../api/inventoryApi';
import { updateLifecycleProfile } from '../../recon-hub/api/lifecycleApi';
import offlineEngine from '../../../shared/services/offlineEngine';
import SyncChip from '../../../shared/components/SyncChip';
import InventoryBottomBar from '../../../shared/components/InventoryBottomBar';
import OutdoorToggle, { useOutdoorMode } from '../../../shared/components/OutdoorToggle';
import CustomizeViewDrawer, { useCustomizeView } from '../components/CustomizeViewDrawer';
import PartNumbersList from '../../../shared/components/PartNumbersList';
import AssetTagsList from '../../../shared/components/AssetTagsList';
import { getMaterialSuggestion } from '../../../shared/utils/toolMaterialUtils';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './ToolDetailScreen.css';

/* ── Fields update_tool may receive (excludes part_numbers, asset_tags, photos) ── */
/* A save sends only the ones a person edited (editedRef), never the whole list. */
/* Lifecycle fields removed — they route to LifecycleProfile via manage_lifecycle_profile */
const SAVEABLE_FIELDS = [
  'supplier_tool_id', 'alt_tool_id', 'cspc', 'barcode', 'qr_code', 'tool_type', 'oem', 'material', 'quantity', 'die_op',
  'tool_code', 'cavities', 'site_location', 'detail_location',
  'row_nbr', 'stack_nbr', 'rack_nbr', 'shelf_nbr', 'skid_nbr',
  'found', 'added_by_tmg', 'inv_oem_pn_status',
  'inv_asset_tag_status', 'length', 'width', 'height', 'estimated_weight',
  'weight_unit', 'inventory_comments',
  // Additive fields from Phase 2 field-parity sweep
  'platform', 'part_description', 'tool_description', 'list_number',
  'tier_level', 'tier_duns', 'ship_to_location', 'last_ship_date',
  'capacity_flag', 'npn_flag',
  'retained_reason', 'tmg_data_comments',
];

/* An edited location number is sent only when the form holds a value or the
   stored document already carries the key, so emptying one the tool never had
   stamps no empty string onto it. */
const NBR_FIELDS = ['row_nbr', 'stack_nbr', 'rack_nbr', 'shelf_nbr', 'skid_nbr'];

/* ── Lifecycle fields — sent to LifecycleProfile, not Tools ───────────────── */
const LIFECYCLE_FIELDS = [
  'current_condition_score', 'warranty_shots', 'warranty_shots_source',
  'actual_shots_to_date', 'weekly_capacity', 'cavity_number_start',
  'cavity_number_end', 'lifecycle_detail', 'assessment_notes',
];

function blankForm() {
  return {
    supplier_tool_id: '', alt_tool_id: '', cspc: '', barcode: '', qr_code: '',
    tool_type: '', oem: '', material: '', quantity: 1, die_op: '', tool_code: '', cavities: '',
    site_location: '', detail_location: '',
    row_nbr: '', stack_nbr: '', rack_nbr: '', shelf_nbr: '', skid_nbr: '',
    found: false, added_by_tmg: true,
    inv_oem_pn_status: '', inv_asset_tag_status: '',
    part_numbers: [], asset_tags: [], photos: [],
    length: '', width: '', height: '', estimated_weight: '', weight_unit: 'LBS',
    inventory_comments: '',
    current_condition_score: null, warranty_shots: '', warranty_shots_source: '',
    actual_shots_to_date: '', weekly_capacity: '',
    cavity_number_start: '', cavity_number_end: '',
    lifecycle_detail: '', assessment_notes: '',
    // Phase 2 additive fields
    platform: '', part_description: '', tool_description: '', list_number: '',
    tier_level: '', tier_duns: '', ship_to_location: '', last_ship_date: '',
    capacity_flag: false, npn_flag: false,
    retained_reason: '', tmg_data_comments: '',
  };
}

/* The form shows what is stored. An absent quantity or unit loads blank, never
   a default: a save sends only edited fields, so a displayed default would be a
   value the screen shows and nothing saves. */
function rawToForm(raw) {
  return {
    supplier_tool_id: raw.supplier_tool_id || '',
    alt_tool_id: raw.alt_tool_id || '',
    cspc: raw.cspc || '',
    barcode: raw.barcode || '',
    qr_code: raw.qr_code || '',
    tool_type: raw.tool_type || '',
    oem: raw.oem || '',
    material: raw.material || '',
    quantity: raw.quantity ?? '',
    die_op: raw.die_op || '',
    tool_code: raw.tool_code || '',
    cavities: raw.cavities ?? '',
    site_location: raw.site_location || '',
    detail_location: raw.detail_location || '',
    row_nbr: raw.row_nbr ?? '',
    stack_nbr: raw.stack_nbr ?? '',
    rack_nbr: raw.rack_nbr ?? '',
    shelf_nbr: raw.shelf_nbr ?? '',
    skid_nbr: raw.skid_nbr ?? '',
    found: !!raw.found,
    added_by_tmg: !!raw.added_by_tmg,
    inv_oem_pn_status: raw.inv_oem_pn_status || '',
    inv_asset_tag_status: raw.inv_asset_tag_status || '',
    part_numbers: raw.part_numbers || [],
    asset_tags: raw.asset_tags || [],
    photos: raw.photos || [],
    length: raw.length ?? '',
    width: raw.width ?? '',
    height: raw.height ?? '',
    estimated_weight: raw.estimated_weight ?? '',
    weight_unit: raw.weight_unit || '',
    inventory_comments: raw.inventory_comments || '',
    current_condition_score: raw.current_condition_score ?? null,
    warranty_shots: raw.warranty_shots ?? '',
    warranty_shots_source: raw.warranty_shots_source || '',
    actual_shots_to_date: raw.actual_shots_to_date ?? '',
    weekly_capacity: raw.weekly_capacity ?? '',
    cavity_number_start: raw.cavity_number_start ?? '',
    cavity_number_end: raw.cavity_number_end ?? '',
    lifecycle_detail: raw.lifecycle_detail || '',
    assessment_notes: raw.assessment_notes || '',
    // Phase 2 additive fields
    platform: raw.platform || '',
    part_description: raw.part_description || '',
    tool_description: raw.tool_description || '',
    list_number: raw.list_number || '',
    tier_level: raw.tier_level || '',
    tier_duns: raw.tier_duns || '',
    ship_to_location: raw.ship_to_location || '',
    last_ship_date: raw.last_ship_date || '',
    capacity_flag: !!raw.capacity_flag,
    npn_flag: !!raw.npn_flag,
    retained_reason: raw.retained_reason || '',
    tmg_data_comments: raw.tmg_data_comments || '',
  };
}

/* ── Panel order — drag-to-reorder persistence ───────────────────────────── */
const DEFAULT_ORDER = [
  'tool_identification', 'location', 'photos', 'part_numbers',
  'asset_tags', 'physical', 'supply_chain', 'flags', 'disposal_context',
  'notes', 'lifecycle_data',
];

function defaultOrderForMode(mode) {
  return mode === 'lifecycle' ? DEFAULT_ORDER : DEFAULT_ORDER.filter(k => k !== 'lifecycle_data');
}

function loadPanelOrder(email, mode) {
  try {
    const raw = localStorage.getItem(`tmg_inv_panel_order_${email || 'default'}`);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    if (!Array.isArray(stored)) return null;
    const required = defaultOrderForMode(mode);
    // Validate every required key is present (version-resilience: new sections fall back to default)
    if (!required.every(k => stored.includes(k))) return null;
    // Filter stored to only include valid keys for current mode
    return stored.filter(k => required.includes(k));
  } catch { return null; }
}

function savePanelOrder(email, order) {
  try { localStorage.setItem(`tmg_inv_panel_order_${email || 'default'}`, JSON.stringify(order)); } catch { /* ignore */ }
}

/* ── Collapsible Section (drag-to-reorder) ───────────────────────────────── */
function Section({ title, sectionKey, collapsed, onToggle, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sectionKey });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={`td-section${isDragging ? ' td-section-dragging' : ''}`}>
      <div className="td-section-header-row">
        <span className="td-drag-handle" {...attributes} {...listeners} aria-label="Reorder section">&#10495;</span>
        <button className="td-section-header" onClick={() => onToggle(sectionKey)}>
          <span className="td-section-title">{title}</span>
          <span className={`td-section-chevron ${collapsed ? '' : 'open'}`}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </button>
      </div>
      {!collapsed && <div className="td-section-body">{children}</div>}
    </div>
  );
}

/* ── Tap Button Group ────────────────────────────────────────────────────── */
function TapButtons({ options, value, onChange, size = 'normal' }) {
  return (
    <div className={`td-tap-group ${size === 'large' ? 'td-tap-large' : ''}`}>
      {options.map(opt => (
        <button
          key={opt.value}
          className={`td-tap-btn ${value === opt.value ? 'active' : ''} ${opt.cls || ''}`}
          onClick={() => onChange(opt.value)}
          type="button"
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */

/* Derive branch base, e.g. "/disposal/inventory" from "/disposal/inventory/SITE/TOOL" */
function branchInventoryBase(pathname) {
  const match = pathname.match(/^(\/[^/]+)\/inventory/);
  return match ? `${match[1]}/inventory` : '/disposal/inventory';
}

export default function ToolDetailScreen() {
  const { siteId, toolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { callerEmail } = useUser();
  const invBase = branchInventoryBase(location.pathname);

  const mode = location.pathname.startsWith('/lifecycle') ? 'lifecycle' : 'disposal';
  const [siteName, setSiteName] = useState(location.state?.siteName || '');
  const customizeView = useCustomizeView(siteId);
  const [showCustomize, setShowCustomize] = useState(false);

  /* ── Scroll to top on tool change (fresh view every time Bob opens a new tool) ── */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [toolId]);

  /* ── Resolve site name if not passed via route state ─────────────────── */
  useEffect(() => {
    if (location.state?.siteName) return;
    getSites(callerEmail).then(sites => {
      const site = sites.find(s => s.site_id === siteId);
      if (site) {
        const name = site.site_city
          ? `${site.site_name} (${site.site_city})`
          : site.supplier_site_code
            ? `${site.site_name} (${site.supplier_site_code})`
            : site.site_name || '';
        setSiteName(name);
      }
    }).catch(() => {});
  }, [siteId, callerEmail, location.state?.siteName]);
  const hiddenSections = customizeView.hiddenPanels;

  const isNew = toolId === 'new';

  /* ── Form state ────────────────────────────────────────────────────────── */
  const [form, setForm] = useState(blankForm);
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState(null);

  // The internal Cosmos tool_id field (may differ from the Cosmos document "id").
  // All sub-resource APIs (media, part numbers, asset tags) must use this value.
  const [internalToolId, setInternalToolId] = useState(null);
  // The tool's real site_id from Cosmos (may differ from URL param for legacy sites)
  const [toolSiteId, setToolSiteId] = useState(null);
  // Which of the five location numbers the stored document carries (see NBR_FIELDS).
  const nbrKeysOnDocRef = useRef(new Set());
  // The fields a person edited since the tool loaded. set() adds to it and both
  // loads reset it; handleSave sends only these, so a value the form displays
  // but nobody chose never reaches update_tool.
  const editedRef = useRef(new Set());

  // Use tool's real site_id from Cosmos (falls back to URL param until loaded)
  const refSiteId = toolSiteId || siteId;
  const { options: refOptions, addValue: addRefValue } = useReferenceData(
    ['part_number_type', 'site_location', 'detail_location', 'tool_type', 'material', 'oem', 'tool_type_material_map'], callerEmail, refSiteId
  );

  useEffect(() => {
    if (!refOptions.tool_type_material_map) return;
    // Fills material only for a tool type the person picked, through set() so it
    // ships. A load never fills it: a <select> cannot re-confirm the value it
    // already shows, so a suggestion displayed on load could never be accepted.
    if (!editedRef.current.has('tool_type')) return;
    const suggestion = getMaterialSuggestion(
      form.tool_type,
      form.material,
      refOptions.tool_type_material_map
    );
    if (suggestion) set('material', suggestion);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- material suggestion effect intentionally excludes form.material and refOptions.tool_type_material_map to prevent infinite loop (effect sets material -> dep change -> re-trigger)
  }, [form.tool_type]);

  /* ── Load tool from API ────────────────────────────────────────────────── */
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    getToolDetail(toolId, callerEmail)
      .then(raw => {
        if (!cancelled) {
          setForm(rawToForm(raw));
          editedRef.current = new Set();
          nbrKeysOnDocRef.current = new Set(NBR_FIELDS.filter(k => k in raw));
          // Capture the real internal tool_id from the Cosmos document
          const realToolId = raw.tool_id || raw.id || toolId;
          setInternalToolId(realToolId);
          // Capture the tool's real site_id for ReferenceData scoping
          if (raw.site_id) setToolSiteId(raw.site_id);
        }
      })
      .catch(err => { if (!cancelled) setLoadError(err.message || 'Failed to load tool'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [toolId, callerEmail, isNew]);

  /* ── Outdoor (high-contrast) mode for field use ───────────────────────── */
  const { outdoor, toggleOutdoor } = useOutdoorMode();

  /* ── Collapsed sections ────────────────────────────────────────────────── */
  const [collapsedSections, setCollapsedSections] = useState(new Set());

  /* ── Panel order (drag-to-reorder) ─────────────────────────────────────── */
  const [panelOrder, setPanelOrder] = useState(
    () => loadPanelOrder(callerEmail, mode) || defaultOrderForMode(mode)
  );

  // Re-derive order when mode or user changes (disposal <-> lifecycle, login change)
  useEffect(() => {
    setPanelOrder(loadPanelOrder(callerEmail, mode) || defaultOrderForMode(mode));
  }, [callerEmail, mode]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setPanelOrder(prev => {
        const oldIdx = prev.indexOf(active.id);
        const newIdx = prev.indexOf(over.id);
        const next = arrayMove(prev, oldIdx, newIdx);
        savePanelOrder(callerEmail, next);
        return next;
      });
    }
  }

  function handleResetOrder() {
    try { localStorage.removeItem(`tmg_inv_panel_order_${callerEmail || 'default'}`); } catch { /* ignore */ }
    setPanelOrder(defaultOrderForMode(mode));
  }

  function toggleCollapse(key) {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  /* ── Part Numbers (API-backed) ─────────────────────────────────────────── */
  const [partNumbers, setPartNumbers] = useState([]);
  const [pnLoading, setPnLoading] = useState(false);
  const [pnError, setPnError] = useState(null);
  const [newPn, setNewPn] = useState('');
  const [newPnType, setNewPnType] = useState('TBD');
  const [newPnDesc, setNewPnDesc] = useState('');

  async function loadPartNumbers() {
    if (!internalToolId) return;
    setPnLoading(true);
    setPnError(null);
    try {
      const data = await getPartNumbers(internalToolId, callerEmail);
      setPartNumbers(data || []);
    } catch (err) {
      setPnError(err.message);
    } finally {
      setPnLoading(false);
    }
  }

  useEffect(() => {
    if (!isNew && internalToolId) loadPartNumbers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalToolId, callerEmail, isNew]);

  async function handleAddPartNumber() {
    if (!newPn.trim() || !internalToolId) return;
    setPnError(null);
    try {
      await addPartNumber({
        toolId: internalToolId, siteId, partNumber: newPn.trim(),
        partNumberType: newPnType, partDescription: newPnDesc, callerEmail,
      });
      setNewPn(''); setNewPnType('TBD'); setNewPnDesc('');
      await loadPartNumbers();
    } catch (err) {
      setPnError(err.message);
    }
  }

  async function handleEditPartNumber(partNumberId, { part_number, part_number_type, part_description }) {
    setPnError(null);
    try {
      const updated = await patchPartNumber({
        partNumberId,
        partNumber: part_number,
        partNumberType: part_number_type,
        partDescription: part_description,
        callerEmail,
      });
      setPartNumbers(prev => prev.map(p => p.id === partNumberId ? updated : p));
    } catch (err) {
      setPnError(err.message);
      throw err;
    }
  }

  async function handleDeletePartNumber(partNumberId) {
    if (!internalToolId) return;
    setPnError(null);
    try {
      await deletePartNumber({ partNumberId, toolId: internalToolId, callerEmail });
      await loadPartNumbers();
    } catch (err) {
      setPnError(err.message);
    }
  }

  /* ── Asset Tags (API-backed) ─────────────────────────────────────────── */
  const [assetTags, setAssetTags] = useState([]);
  const [atLoading, setAtLoading] = useState(false);
  const [atError, setAtError] = useState(null);
  const [newTag, setNewTag] = useState('');

  async function loadAssetTags() {
    if (!internalToolId) return;
    setAtLoading(true);
    setAtError(null);
    try {
      const data = await getAssetTags(internalToolId, callerEmail);
      setAssetTags(data || []);
    } catch (err) {
      setAtError(err.message);
    } finally {
      setAtLoading(false);
    }
  }

  useEffect(() => {
    if (!isNew && internalToolId) loadAssetTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalToolId, callerEmail, isNew]);

  async function handleAddAssetTag() {
    if (!newTag.trim() || !internalToolId) return;
    setAtError(null);
    try {
      await addAssetTag({ toolId: internalToolId, siteId, tagValue: newTag.trim(), callerEmail });
      setNewTag('');
      await loadAssetTags();
    } catch (err) {
      setAtError(err.message);
    }
  }

  async function handleEditAssetTag(tagId, { tag_value }) {
    setAtError(null);
    try {
      const updated = await patchAssetTag({ tagId, tagValue: tag_value, callerEmail });
      setAssetTags(prev => prev.map(t => t.id === tagId ? updated : t));
    } catch (err) {
      setAtError(err.message);
      throw err;
    }
  }

  async function handleDeleteAssetTag(tagId) {
    if (!internalToolId) return;
    setAtError(null);
    try {
      await deleteAssetTag({ tagId, toolId: internalToolId, callerEmail });
      await loadAssetTags();
    } catch (err) {
      setAtError(err.message);
    }
  }

  /* ── Photos (API-backed via offlineEngine) ────────────────────────────── */
  const MAX_PHOTOS = 25;
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [photosError, setPhotosError] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const cameraRef = useRef(null);
  const uploadRef = useRef(null);

  // Phase C (lightbox sprint): converted from
  // window.history.pushState/popstate to React Router
  // replace-semantics, mirroring <PhotoGalleryPanel>.
  // Eliminates the X-button bubble bug where closeLightbox firing
  // twice (button click + backdrop click bubble) overshot a real
  // history entry via window.history.back(). Does NOT migrate the
  // photo gallery / upload / delete chrome — that stays separate
  // from <PhotoGalleryPanel> by design (panel has no editPhotos
  // mode, no per-photo delete, no upload queue).
  const HASH_SENTINEL = '#tool-photo';

  const handleOpenLightbox = useCallback((url) => {
    setLightboxUrl(url);
    if (location.hash !== HASH_SENTINEL) {
      navigate(
        `${location.pathname}${location.search}${HASH_SENTINEL}`,
        { replace: true }
      );
    }
  }, [location, navigate, HASH_SENTINEL]);

  const closeLightbox = useCallback(() => {
    setLightboxUrl(null);
    if (location.hash === HASH_SENTINEL) {
      navigate(
        `${location.pathname}${location.search}`,
        { replace: true }
      );
    }
  }, [location, navigate, HASH_SENTINEL]);

  // Escape closes lightbox while open.
  useEffect(() => {
    if (!lightboxUrl) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') closeLightbox(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lightboxUrl, closeLightbox]);

  // Silent close when URL changes to one without the sentinel —
  // handles browser-back from open lightbox.
  useEffect(() => {
    if (location.hash !== HASH_SENTINEL && lightboxUrl) {
      setLightboxUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const [editPhotos, setEditPhotos] = useState(false);

  async function handleDeletePhoto(mediaId) {
    try {
      await deleteMedia(mediaId, callerEmail);
      setPhotos(prev => prev.filter(p => p.id !== mediaId && p.media_id !== mediaId));
    } catch (err) {
      setPhotosError(err.message);
    }
  }

  // Sequential upload queue — array of files + boolean guard
  const uploadQueueRef = useRef([]);   // array of { file, tempId, localUrl }
  const isUploadingRef = useRef(false);

  async function loadPhotos() {
    if (!internalToolId) return;
    setPhotosLoading(true);
    setPhotosError(null);
    try {
      const data = await getMedia(internalToolId, callerEmail);
      setPhotos(data || []);
    } catch (err) {
      setPhotosError(err.message);
    } finally {
      setPhotosLoading(false);
    }
  }

  useEffect(() => {
    if (!isNew && internalToolId) loadPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalToolId, callerEmail, isNew]);

  async function processUploadQueue() {
    if (isUploadingRef.current) return;
    if (uploadQueueRef.current.length === 0) return;

    isUploadingRef.current = true;

    const { file, tempId, localUrl } = uploadQueueRef.current.shift();

    try {
      const result = await offlineEngine.enqueuePhoto({
        toolId: internalToolId,
        disposalId: null,
        photoType: 'tool_photo',
        file,
        uploadedBy: callerEmail,
        siteId,
        callerEmail,
      });

      if (!result.queued && result.blob_url) {
        setPhotos(prev => prev.map(p =>
          p.id === tempId ? { ...p, id: result.media_id, blob_url: result.blob_url, _local: false } : p
        ));
        URL.revokeObjectURL(localUrl);
      }
    } catch (err) {
      setPhotos(prev => prev.filter(p => p.id !== tempId));
      URL.revokeObjectURL(localUrl);
      setPhotosError(err.message);
    }

    isUploadingRef.current = false;

    // Recurse to process next item (if any)
    processUploadQueue();
  }

  async function enqueueUpload(file) {
    // Read file bytes immediately — the File reference from a camera input
    // can be invalidated or overwritten by the next capture on mobile browsers.
    const arrayBuffer = await file.arrayBuffer();
    const snapshot = new File([arrayBuffer], file.name, { type: file.type });

    const localUrl = URL.createObjectURL(snapshot);
    const tempId = `temp-${Date.now()}-${Math.random()}`;

    // Add optimistic thumbnail immediately
    setPhotos(prev => {
      if (prev.length >= MAX_PHOTOS) return prev;
      return [...prev, { id: tempId, blob_url: localUrl, _local: true }];
    });

    // Push snapshot (not original) to queue, then kick the processor
    uploadQueueRef.current.push({ file: snapshot, tempId, localUrl });
    processUploadQueue();
  }

  function handlePhotoCapture(e) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      enqueueUpload(file);
    }
    e.target.value = '';
  }

  /* ── Save to Cosmos ───────────────────────────────────────────────────── */
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null); // { type: 'success'|'error', text }
  const savingRef = useRef(false);
  const formRef = useRef(form);
  formRef.current = form;

  async function handleSave() {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveMsg(null);

    // Read form from ref to avoid stale closure
    const currentForm = formRef.current;

    // Build payload from the edited saveable fields, coerce numeric strings
    const payload = {};
    for (const key of SAVEABLE_FIELDS) {
      if (!editedRef.current.has(key)) continue;
      let val = currentForm[key];
      // Deliberately narrowed for the five only - see NBR_FIELDS.
      if (NBR_FIELDS.includes(key) && (val === '' || val == null) && !nbrKeysOnDocRef.current.has(key)) continue;
      if (['quantity', 'cavities', 'length', 'width', 'height',
           'estimated_weight'].includes(key)) {
        val = val === '' || val === null || val === undefined ? null : Number(val);
      }
      payload[key] = val;
    }

    try {
      // Nothing edited means nothing to write: update_tool refuses an empty
      // patch, and the lifecycle save below still runs.
      if (Object.keys(payload).length > 0) {
        await updateInventoryTool(internalToolId || toolId, payload, callerEmail);
        // A key just written now exists on the document, so a later clear is sent.
        for (const key of NBR_FIELDS) if (key in payload) nbrKeysOnDocRef.current.add(key);
      }

      // Persist new location values to ReferenceData so they appear as suggestions
      for (const field of ['site_location', 'detail_location']) {
        const val = currentForm[field]?.trim();
        if (val) addRefValue(field, val, refSiteId).catch(() => {});
      }

      // ── Lifecycle fields → LifecycleProfile container ──────────────────
      if (mode === 'lifecycle') {
        const lcPayload = {};
        const LC_NUMERIC = [
          'current_condition_score', 'warranty_shots', 'actual_shots_to_date',
          'weekly_capacity', 'cavity_number_start', 'cavity_number_end',
        ];
        for (const key of LIFECYCLE_FIELDS) {
          let val = currentForm[key];
          if (LC_NUMERIC.includes(key)) {
            val = val === '' || val === null || val === undefined ? null : Number(val);
          }
          if (val !== '' && val != null) {
            lcPayload[key] = val;
          }
        }
        if (Object.keys(lcPayload).length > 0) {
          try {
            await updateLifecycleProfile(internalToolId || toolId, lcPayload, callerEmail);
          } catch (lcErr) {
            console.error('Lifecycle save failed:', lcErr);
            setSaveMsg({ type: 'error', text: `Tools saved, but lifecycle save failed: ${lcErr.message}` });
            savingRef.current = false;
            setSaving(false);
            return;
          }
        }
      }

      setSaveMsg({ type: 'success', text: 'Saved' });
      setTimeout(() => setSaveMsg(null), 2500);
      // Return to tool list so Bob can move to the next tool immediately.
      // 500ms lets the success toast register before the screen transitions.
      setTimeout(() => navigate(-1), 500);
    } catch (err) {
      setSaveMsg({ type: 'error', text: err.message });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  /* ── Field helpers ─────────────────────────────────────────────────────── */
  // Every edit goes through here, and this is what marks a field for the save.
  function set(field, value) {
    editedRef.current.add(field);
    setForm(prev => ({ ...prev, [field]: value }));
  }

  /* ── Bottom bar toast ─────────────────────────────────────────────────── */
  const [bbToast, setBbToast] = useState(null);
  function showBbToast(msg) { setBbToast(msg); setTimeout(() => setBbToast(null), 2000); }

  /* ── Visible-section check ─────────────────────────────────────────────── */
  // A panel hides when CustomizeViewDrawer has it in hiddenPanels — which the
  // hook auto-sets when every field inside is toggled off. So hiddenPanels is
  // the single source of truth for panel visibility.
  function sectionVisible(key) {
    return !hiddenSections.has(key);
  }

  const isFieldVisible = customizeView.isFieldVisible;

  return (
    <div className={`td-screen${outdoor ? ' outdoor-mode' : ''}`}>
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <header className="td-header">
        <button className="td-back" onClick={() => navigate(`${invBase}/${siteId}`, { state: { mode, siteName } })}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="td-header-text">
          <h1 className="td-header-title">{loading ? '...' : (form.supplier_tool_id || toolId)}</h1>
          <p className="td-header-sub">{siteName || '...'}</p>
        </div>
        <button className="td-btn-customize" onClick={() => setShowCustomize(true)} aria-label="Customize view">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M3 5H17M3 10H17M3 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="7" cy="5" r="2" fill="currentColor"/>
            <circle cx="13" cy="10" r="2" fill="currentColor"/>
            <circle cx="9" cy="15" r="2" fill="currentColor"/>
          </svg>
        </button>
        <OutdoorToggle outdoor={outdoor} onToggle={toggleOutdoor} />
        <SyncChip />
      </header>

      {/* ── Save toast ────────────────────────────────────────────────────── */}
      {saveMsg && (
        <div className={`td-toast ${saveMsg.type === 'error' ? 'td-toast-error' : ''}`}>
          {saveMsg.text}
        </div>
      )}

      {/* ── Loading / Error ──────────────────────────────────────────────── */}
      {loading && (
        <div className="td-loading">Loading tool...</div>
      )}
      {!loading && loadError && (
        <div className="td-load-error">
          <p className="td-load-error-msg">Failed to load tool</p>
          <p className="td-load-error-detail">{loadError}</p>
          <button className="td-load-error-retry" onClick={() => {
            setLoadError(null);
            setLoading(true);
            getToolDetail(toolId, callerEmail)
              .then(raw => {
                nbrKeysOnDocRef.current = new Set(NBR_FIELDS.filter(k => k in raw));
                editedRef.current = new Set();
                setForm(rawToForm(raw));
              })
              .catch(err => setLoadError(err.message))
              .finally(() => setLoading(false));
          }}>Retry</button>
        </div>
      )}

      {/* ── Form body ────────────────────────────────────────────────────── */}
      {!loading && !loadError && <div className="td-body">

        {/* ─ Sections (drag-to-reorder) ──────────────────────────────────── */}
        {(() => {
          const sectionMap = {};

          if (sectionVisible('tool_identification')) sectionMap.tool_identification = (
            <Section key="tool_identification" title="Tool Identification" sectionKey="tool_identification"
              collapsed={collapsedSections.has('tool_identification')} onToggle={toggleCollapse}>
              <TapButtons size="large" value={form.found ? 'FOUND' : (form.added_by_tmg ? 'ADDED' : null)}
                onChange={v => {
                  if (v === 'FOUND') { set('found', true); set('added_by_tmg', false); }
                  else { set('found', false); set('added_by_tmg', true); }
                }}
                options={[
                  { value: 'FOUND', label: 'Found', cls: 'green' },
                  { value: 'ADDED', label: 'Added by TMG', cls: 'purple' },
                ]}
              />
              <label className="td-field">
                <span className="td-label">Supplier Tool ID</span>
                <input type="text" className="td-input" value={form.supplier_tool_id}
                  onChange={e => set('supplier_tool_id', e.target.value)} />
              </label>
{isFieldVisible('alt_tool_id') && (
              <label className="td-field">
                <span className="td-label">Alt Tool ID</span>
                <input type="text" className="td-input" value={form.alt_tool_id}
                  onChange={e => set('alt_tool_id', e.target.value)} />
              </label>
              )}
{isFieldVisible('cspc') && (
              <label className="td-field">
                <span className="td-label">CSPC</span>
                <input type="text" className="td-input" value={form.cspc}
                  onChange={e => set('cspc', e.target.value)} />
              </label>
              )}
{isFieldVisible('barcode') && (
              <label className="td-field">
                <span className="td-label">Barcode</span>
                <input type="text" className="td-input" value={form.barcode}
                  onChange={e => set('barcode', e.target.value)} />
              </label>
              )}
{isFieldVisible('qr_code') && (
              <label className="td-field">
                <span className="td-label">QR Code</span>
                <input type="text" className="td-input" value={form.qr_code}
                  onChange={e => set('qr_code', e.target.value)} />
              </label>
              )}
{isFieldVisible('tool_type') && (
              <label className="td-field">
                <span className="td-label">Tool Type</span>
                <select className="td-select" value={form.tool_type}
                  onChange={e => set('tool_type', e.target.value)}>
                  <option value="">— Select —</option>
                  {(refOptions.tool_type || []).map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </label>
              )}
{isFieldVisible('oem') && (
              <label className="td-field">
                <span className="td-label">OEM</span>
                <FixedListSelect value={form.oem}
                  onChange={v => set('oem', v)}
                  options={refOptions.oem || []}
                  placeholder="— Select —"
                  className="td-select" />
              </label>
              )}
{isFieldVisible('material') && (
              <label className="td-field">
                <span className="td-label">Material</span>
                <select className="td-select" value={form.material}
                  onChange={e => set('material', e.target.value)}>
                  <option value="">— Select —</option>
                  {(refOptions.material || []).map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </label>
              )}
              <div className="td-row-2">
{isFieldVisible('quantity') && (
                <label className="td-field">
                  <span className="td-label">Quantity</span>
                  <input type="number" className="td-input" value={form.quantity}
                    onChange={e => set('quantity', e.target.value)} />
                </label>
                )}
{isFieldVisible('cavities') && (
                <label className="td-field">
                  <span className="td-label">Cavities</span>
                  <input type="number" className="td-input" value={form.cavities ?? ''}
                    onChange={e => set('cavities', e.target.value)} />
                </label>
                )}
              </div>
              {mode === 'lifecycle' && (
                <div className="td-row-2">
{isFieldVisible('cavity_number_start') && (
                  <label className="td-field">
                    <span className="td-label">Cavity # Start</span>
                    <input type="number" className="td-input" value={form.cavity_number_start ?? ''}
                      onChange={e => set('cavity_number_start', e.target.value)} />
                  </label>
                  )}
{isFieldVisible('cavity_number_end') && (
                  <label className="td-field">
                    <span className="td-label">Cavity # End</span>
                    <input type="number" className="td-input" value={form.cavity_number_end ?? ''}
                      onChange={e => set('cavity_number_end', e.target.value)} />
                  </label>
                  )}
                </div>
              )}
              <div className="td-row-2">
{isFieldVisible('die_op') && (
                <label className="td-field">
                  <span className="td-label">Die / Op</span>
                  <input type="text" className="td-input" value={form.die_op}
                    onChange={e => set('die_op', e.target.value)} />
                </label>
                )}
{isFieldVisible('tool_code') && (
                <label className="td-field">
                  <span className="td-label">Tool Code</span>
                  <input type="text" className="td-input" value={form.tool_code}
                    onChange={e => set('tool_code', e.target.value)} />
                </label>
                )}
              </div>
{isFieldVisible('platform') && (
              <label className="td-field">
                <span className="td-label">Platform</span>
                <input type="text" className="td-input" value={form.platform}
                  onChange={e => set('platform', e.target.value)} />
              </label>
              )}
{isFieldVisible('list_number') && (
              <label className="td-field">
                <span className="td-label">List Number</span>
                <input type="text" className="td-input" value={form.list_number}
                  onChange={e => set('list_number', e.target.value)} />
              </label>
              )}
{isFieldVisible('tool_description') && (
              <label className="td-field">
                <span className="td-label">Tool Description</span>
                <input type="text" className="td-input" value={form.tool_description}
                  onChange={e => set('tool_description', e.target.value)} />
              </label>
              )}
{isFieldVisible('part_description') && (
              <label className="td-field">
                <span className="td-label">Part Description</span>
                <input type="text" className="td-input" value={form.part_description}
                  onChange={e => set('part_description', e.target.value)} />
              </label>
              )}
            </Section>
          );

          if (sectionVisible('location')) sectionMap.location = (
            <Section key="location" title="Location" sectionKey="location"
              collapsed={collapsedSections.has('location')} onToggle={toggleCollapse}>
              <div className="td-row-2">
{isFieldVisible('site_location') && (
                <label className="td-field">
                  <span className="td-label">Site Location</span>
                  <ComboBox value={form.site_location}
                    onChange={v => set('site_location', v)}
                    options={refOptions.site_location || []}
                    placeholder="Enter or select location"
                    className="td-input" />
                </label>
                )}
{isFieldVisible('detail_location') && (
                <label className="td-field">
                  <span className="td-label">Detail Location</span>
                  <ComboBox value={form.detail_location}
                    onChange={v => set('detail_location', v)}
                    options={refOptions.detail_location || []}
                    placeholder="Enter or select section"
                    className="td-input" />
                </label>
                )}
              </div>
              <div className="td-row-2">
                {/* Editable panel: all five always render (Customize View can still hide one). */}
{isFieldVisible('row_nbr') && (
                <label className="td-field">
                  <span className="td-label">Row Nbr</span>
                  <input type="text" className="td-input" value={form.row_nbr}
                    onChange={e => set('row_nbr', e.target.value)} />
                </label>
                )}
{isFieldVisible('stack_nbr') && (
                <label className="td-field">
                  <span className="td-label">Stack Nbr</span>
                  <input type="text" className="td-input" value={form.stack_nbr}
                    onChange={e => set('stack_nbr', e.target.value)} />
                </label>
                )}
{isFieldVisible('rack_nbr') && (
                <label className="td-field">
                  <span className="td-label">Rack Nbr</span>
                  <input type="text" className="td-input" value={form.rack_nbr}
                    onChange={e => set('rack_nbr', e.target.value)} />
                </label>
                )}
{isFieldVisible('shelf_nbr') && (
                <label className="td-field">
                  <span className="td-label">Shelf Nbr</span>
                  <input type="text" className="td-input" value={form.shelf_nbr}
                    onChange={e => set('shelf_nbr', e.target.value)} />
                </label>
                )}
{isFieldVisible('skid_nbr') && (
                <label className="td-field">
                  <span className="td-label">Skid Nbr</span>
                  <input type="text" className="td-input" value={form.skid_nbr}
                    onChange={e => set('skid_nbr', e.target.value)} />
                </label>
                )}
              </div>
            </Section>
          );

          if (sectionVisible('photos')) sectionMap.photos = (
            <Section key="photos" title={`Photos (${photos.length})`} sectionKey="photos"
              collapsed={collapsedSections.has('photos')} onToggle={toggleCollapse}>
              {photos.length > 0 && (
                <button type="button"
                  className={`td-edit-photos-btn${editPhotos ? ' active' : ''}`}
                  onClick={() => setEditPhotos(prev => !prev)}>
                  {editPhotos ? 'Done' : 'Edit Photos'}
                </button>
              )}
              {photosError && <p className="td-inline-error">{photosError}</p>}
              {photosLoading && <p className="td-inline-loading">Loading photos...</p>}
              <div className="td-photo-grid">
                {!photosLoading && photos.map(p => (
                  <div key={p.id} className="td-photo-thumb-wrap">
                    <button type="button" className="td-photo-thumb"
                      onClick={() => {
                        if (editPhotos) return;
                        handleOpenLightbox(p.blob_url);
                      }}>
                      <img src={p.blob_url} alt="" className="td-photo-img" loading="lazy" />
                    </button>
                    {editPhotos && !p._local && (
                      <button type="button" className="td-photo-delete"
                        onClick={() => handleDeletePhoto(p.id || p.media_id)}>×</button>
                    )}
                  </div>
                ))}
                {photos.length >= MAX_PHOTOS ? (
                  <p className="td-photo-limit">Maximum 25 photos reached</p>
                ) : (
                  <>
                    <button type="button" className="td-photo-add"
                      onClick={() => cameraRef.current?.click()}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                      <span>Camera</span>
                    </button>
                    <button type="button" className="td-photo-add"
                      onClick={() => uploadRef.current?.click()}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Upload</span>
                    </button>
                  </>
                )}
              </div>
            </Section>
          );

          if (sectionVisible('part_numbers')) sectionMap.part_numbers = (
            <Section key="part_numbers" title="Part Numbers" sectionKey="part_numbers"
              collapsed={collapsedSections.has('part_numbers')} onToggle={toggleCollapse}>
              <span className="td-label">OEM Part Number Status</span>
              <TapButtons value={form.inv_oem_pn_status}
                onChange={v => set('inv_oem_pn_status', v)}
                options={[
                  { value: 'SEE PIC', label: 'SEE PIC' },
                  { value: 'NO PNR FOUND', label: 'NO PNR FOUND' },
                  { value: 'PNR FOUND', label: 'PNR FOUND' },
                ]}
              />
              <PartNumbersList
                partNumbers={partNumbers}
                loading={pnLoading}
                error={pnError}
                onAdd={handleAddPartNumber}
                onEdit={handleEditPartNumber}
                onDelete={handleDeletePartNumber}
                newPn={newPn}
                setNewPn={setNewPn}
                newPnType={newPnType}
                setNewPnType={setNewPnType}
                newPnDesc={newPnDesc}
                setNewPnDesc={setNewPnDesc}
                partNumberTypeOptions={refOptions.part_number_type}
              />
            </Section>
          );

          if (sectionVisible('asset_tags')) sectionMap.asset_tags = (
            <Section key="asset_tags" title="Asset Tags" sectionKey="asset_tags"
              collapsed={collapsedSections.has('asset_tags')} onToggle={toggleCollapse}>
              <span className="td-label">Asset Tag Status</span>
              <TapButtons value={form.inv_asset_tag_status}
                onChange={v => set('inv_asset_tag_status', v)}
                options={[
                  { value: 'SEE PIC', label: 'SEE PIC' },
                  { value: 'NOT TAGGED', label: 'NOT TAGGED' },
                  { value: 'TAGGED', label: 'TAGGED' },
                ]}
              />
              <AssetTagsList
                assetTags={assetTags}
                loading={atLoading}
                error={atError}
                onAdd={handleAddAssetTag}
                onEdit={handleEditAssetTag}
                onDelete={handleDeleteAssetTag}
                newTag={newTag}
                setNewTag={setNewTag}
              />
            </Section>
          );

          if (sectionVisible('physical')) sectionMap.physical = (
            <Section key="physical" title="Physical" sectionKey="physical"
              collapsed={collapsedSections.has('physical')} onToggle={toggleCollapse}>
              <div className="td-row-3">
{isFieldVisible('length') && (
                <label className="td-field">
                  <span className="td-label">Length</span>
                  <input type="number" className="td-input" value={form.length ?? ''}
                    onChange={e => set('length', e.target.value)} />
                </label>
                )}
{isFieldVisible('width') && (
                <label className="td-field">
                  <span className="td-label">Width</span>
                  <input type="number" className="td-input" value={form.width ?? ''}
                    onChange={e => set('width', e.target.value)} />
                </label>
                )}
{isFieldVisible('height') && (
                <label className="td-field">
                  <span className="td-label">Height</span>
                  <input type="number" className="td-input" value={form.height ?? ''}
                    onChange={e => set('height', e.target.value)} />
                </label>
                )}
              </div>
              <div className="td-row-2">
{isFieldVisible('estimated_weight') && (
                <label className="td-field">
                  <span className="td-label">Estimated Weight</span>
                  <input type="number" className="td-input" value={form.estimated_weight ?? ''}
                    onChange={e => set('estimated_weight', e.target.value)} />
                </label>
                )}
                <div className="td-field">
{isFieldVisible('weight_unit') && (<>
                  <span className="td-label">Unit</span>
                  <TapButtons value={form.weight_unit}
                    onChange={v => set('weight_unit', v)}
                    options={[
                      { value: 'LBS', label: 'LBS' },
                      { value: 'KG', label: 'KG' },
                      { value: 'TONS', label: 'TONS' },
                    ]}
                  />
                  </>)}
                </div>
              </div>
            </Section>
          );

          if (sectionVisible('supply_chain')) sectionMap.supply_chain = (
            <Section key="supply_chain" title="Supply Chain" sectionKey="supply_chain"
              collapsed={collapsedSections.has('supply_chain')} onToggle={toggleCollapse}>
              <div className="td-row-2">
{isFieldVisible('tier_level') && (
                <label className="td-field">
                  <span className="td-label">Tier Level</span>
                  <input type="text" className="td-input" value={form.tier_level}
                    onChange={e => set('tier_level', e.target.value)} />
                </label>
                )}
{isFieldVisible('tier_duns') && (
                <label className="td-field">
                  <span className="td-label">Tier DUNS</span>
                  <input type="text" className="td-input" value={form.tier_duns}
                    onChange={e => set('tier_duns', e.target.value)} />
                </label>
                )}
              </div>
{isFieldVisible('ship_to_location') && (
              <label className="td-field">
                <span className="td-label">Ship To Location</span>
                <input type="text" className="td-input" value={form.ship_to_location}
                  onChange={e => set('ship_to_location', e.target.value)} />
              </label>
              )}
{isFieldVisible('last_ship_date') && (
              <label className="td-field">
                <span className="td-label">Last Ship Date</span>
                <input type="date" className="td-input" value={form.last_ship_date}
                  onChange={e => set('last_ship_date', e.target.value)} />
              </label>
              )}
            </Section>
          );

          if (sectionVisible('flags')) sectionMap.flags = (
            <Section key="flags" title="Flags" sectionKey="flags"
              collapsed={collapsedSections.has('flags')} onToggle={toggleCollapse}>
{isFieldVisible('capacity_flag') && (<>
              <span className="td-label">Capacity Flag</span>
              <TapButtons value={form.capacity_flag ? 'YES' : 'NO'}
                onChange={v => set('capacity_flag', v === 'YES')}
                options={[
                  { value: 'NO', label: 'No' },
                  { value: 'YES', label: 'Yes', cls: 'amber' },
                ]}
              />
              </>)}
{isFieldVisible('npn_flag') && (<>
              <span className="td-label">NPN Flag</span>
              <TapButtons value={form.npn_flag ? 'YES' : 'NO'}
                onChange={v => set('npn_flag', v === 'YES')}
                options={[
                  { value: 'NO', label: 'No' },
                  { value: 'YES', label: 'Yes', cls: 'amber' },
                ]}
              />
              </>)}
            </Section>
          );

          if (sectionVisible('disposal_context')) sectionMap.disposal_context = (
            <Section key="disposal_context" title="Disposal Context" sectionKey="disposal_context"
              collapsed={collapsedSections.has('disposal_context')} onToggle={toggleCollapse}>
{isFieldVisible('retained_reason') && (
              <label className="td-field">
                <span className="td-label">Retained Reason</span>
                <textarea className="td-textarea" rows={3} placeholder="Reason this tool is retained..."
                  value={form.retained_reason}
                  onChange={e => set('retained_reason', e.target.value)} />
              </label>
              )}
            </Section>
          );

          if (sectionVisible('notes')) sectionMap.notes = (
            <Section key="notes" title="Notes" sectionKey="notes"
              collapsed={collapsedSections.has('notes')} onToggle={toggleCollapse}>
{isFieldVisible('inventory_comments') && (
              <textarea className="td-textarea" rows={4} placeholder="Inventory comments..."
                value={form.inventory_comments}
                onChange={e => set('inventory_comments', e.target.value)} />
              )}
{isFieldVisible('tmg_data_comments') && (
              <label className="td-field">
                <span className="td-label">TMG Data Comments</span>
                <textarea className="td-textarea" rows={3} placeholder="TMG data comments..."
                  value={form.tmg_data_comments}
                  onChange={e => set('tmg_data_comments', e.target.value)} />
              </label>
              )}
            </Section>
          );

          if (mode === 'lifecycle' && sectionVisible('lifecycle_data')) sectionMap.lifecycle_data = (
            <Section key="lifecycle_data" title="Lifecycle Data" sectionKey="lifecycle_data"
              collapsed={collapsedSections.has('lifecycle_data')} onToggle={toggleCollapse}>
{isFieldVisible('current_condition_score') && (<>
              <span className="td-label">Current Condition Score</span>
              <TapButtons size="large" value={form.current_condition_score}
                onChange={v => set('current_condition_score', v)}
                options={[
                  { value: 1, label: '1 Good', cls: 'green' },
                  { value: 2, label: '2 Fair', cls: 'amber' },
                  { value: 3, label: '3 Poor', cls: 'orange' },
                  { value: 4, label: '4 Critical', cls: 'red' },
                ]}
              />
              </>)}
              <div className="td-row-2">
{isFieldVisible('warranty_shots') && (
                <label className="td-field">
                  <span className="td-label">Warranty Pieces</span>
                  <input type="number" className="td-input" value={form.warranty_shots ?? ''}
                    onChange={e => set('warranty_shots', e.target.value)} />
                </label>
                )}
{isFieldVisible('warranty_shots_source') && (
                <label className="td-field">
                  <span className="td-label">Warranty Source</span>
                  <input type="text" className="td-input" value={form.warranty_shots_source}
                    onChange={e => set('warranty_shots_source', e.target.value)} />
                </label>
                )}
              </div>
              <div className="td-row-2">
{isFieldVisible('actual_shots_to_date') && (
                <label className="td-field">
                  <span className="td-label">Actual Pieces</span>
                  <input type="number" className="td-input" value={form.actual_shots_to_date ?? ''}
                    onChange={e => set('actual_shots_to_date', e.target.value)} />
                </label>
                )}
{isFieldVisible('weekly_capacity') && (
                <label className="td-field">
                  <span className="td-label">Weekly Capacity</span>
                  <input type="number" className="td-input" value={form.weekly_capacity ?? ''}
                    onChange={e => set('weekly_capacity', e.target.value)} />
                </label>
                )}
              </div>
{isFieldVisible('lifecycle_detail') && (
              <label className="td-field">
                <span className="td-label">Lifecycle Detail</span>
                <input type="text" className="td-input" value={form.lifecycle_detail}
                  onChange={e => set('lifecycle_detail', e.target.value)} />
              </label>
              )}
{isFieldVisible('assessment_notes') && (
              <label className="td-field">
                <span className="td-label">Assessment Notes</span>
                <textarea className="td-textarea" rows={3} placeholder="Assessment notes..."
                  value={form.assessment_notes}
                  onChange={e => set('assessment_notes', e.target.value)} />
              </label>
              )}
            </Section>
          );

          const orderedIds = panelOrder.filter(id => sectionMap[id]);
          return (
            <>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
                  {orderedIds.map(id => sectionMap[id])}
                </SortableContext>
              </DndContext>
              <button className="td-reset-order" onClick={handleResetOrder} type="button">
                Reset panel order
              </button>
            </>
          );
        })()}

        {/* ── Photo Lightbox (modal overlay — outside reorderable sections) ─ */}
        {lightboxUrl && (
          <div className="td-lightbox" onClick={closeLightbox}>
            <button className="td-lightbox-close" onClick={closeLightbox}>×</button>
            <img src={lightboxUrl} alt="" className="td-lightbox-img" />
          </div>
        )}
      </div>}

      {/* ── Persistent hidden file inputs (must never unmount) ─────────── */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment"
        style={{ display: 'none' }} onChange={handlePhotoCapture} />
      <input ref={uploadRef} type="file" accept="image/*" multiple
        style={{ display: 'none' }} onChange={handlePhotoCapture} />

      {/* ── Customize View Drawer ──────────────────────────────────── */}
      <CustomizeViewDrawer
        open={showCustomize}
        onClose={() => setShowCustomize(false)}
        mode={mode}
        customizeView={customizeView}
      />

      {/* ── Bottom bar toast ──────────────────────────────────────────── */}
      {bbToast && <div className="td-toast">{bbToast}</div>}

      {/* ── Bottom Bar (with Save) ─────────────────────────────────────── */}
      <InventoryBottomBar
        onAdd={() => showBbToast('Go to list to add a tool')}
        onSave={handleSave}
        saving={saving}
        onSearch={() => showBbToast('Go to list to search')}
      />
    </div>
  );
}
